import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, MoreHorizontal, ShieldCheck, HardHat, Handshake, Crown, ClipboardCheck, User as UserIcon, Loader2, Lock } from "lucide-react";
import { roleLabel, formatDate } from "../lib/format";
import { PrintButton, PrintHeader } from "../components/Print";

const roleBadge = (role) => {
  if (role === "owner" || role === "admin") return { cls: "bg-slate-900 text-white", Icon: Crown };
  if (role === "installation_manager") return { cls: "bg-amber-400 text-amber-950", Icon: HardHat };
  if (role === "assistant_installation_manager") return { cls: "bg-amber-100 text-amber-900", Icon: HardHat };
  if (role === "sales_representative") return { cls: "bg-indigo-100 text-indigo-800", Icon: Handshake };
  if (role === "receptionist") return { cls: "bg-teal-100 text-teal-800", Icon: ClipboardCheck };
  return { cls: "bg-slate-200 text-slate-800", Icon: UserIcon };
};

function UserDialog({ open, onOpenChange, user, onSubmit }) {
  const isEdit = !!user;
  const [form, setForm] = useState({ email: "", name: "", role: "warehouse_staff", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: user?.email || "",
        name: user?.name || "",
        role: user?.role || "warehouse_staff",
        password: "",
      });
    }
  }, [open, user]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!isEdit) {
      if (!form.email.trim()) return toast.error("Email is required");
      if (!form.password || form.password.length < 6) return toast.error("Password must be at least 6 characters");
    } else {
      if (form.password && form.password.length < 6) return toast.error("Password must be at least 6 characters");
    }
    setBusy(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {isEdit ? "Edit Teammate" : "Add Teammate"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this teammate's details, role, or reset their password." : "Send them a starter password. They can change it after signing in."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Full Name</Label>
            <Input
              required
              value={form.name}
              onChange={update("name")}
              data-testid="user-name-input"
              className="mt-1.5 rounded-sm"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
            <Input
              type="email"
              required
              disabled={isEdit}
              value={form.email}
              onChange={update("email")}
              data-testid="user-email-input"
              className="mt-1.5 rounded-sm"
              placeholder="jane@jcsupplys.com"
            />
            {isEdit && <p className="mt-1 text-xs text-slate-500">Email can't be changed after account creation.</p>}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Role</Label>
            <Select value={form.role} onValueChange={update("role")}>
              <SelectTrigger data-testid="user-role-select" className="mt-1.5 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="installation_manager">Installation Manager</SelectItem>
                <SelectItem value="assistant_installation_manager">Assistant Installation Manager</SelectItem>
                <SelectItem value="sales_representative">Sales Representative</SelectItem>
                <SelectItem value="warehouse_staff">Warehouse Staff</SelectItem>
                <SelectItem value="receptionist">Receptionist / Office Secretary</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-500">Owner and Installation Managers (senior & assistant) have full control. Sales Reps, Warehouse Staff, and Receptionists can add inventory and log deliveries.</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              {isEdit ? "New Password (optional)" : "Starter Password"}
            </Label>
            <Input
              type="password"
              required={!isEdit}
              value={form.password}
              onChange={update("password")}
              data-testid="user-password-input"
              className="mt-1.5 rounded-sm font-mono"
              placeholder={isEdit ? "Leave blank to keep current password" : "min 6 characters"}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="user-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              {isEdit ? "Save Changes" : "Add Teammate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const { user: currentUser, isManager } = useAuth();
  const isAdmin = isManager;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (form) => {
    if (editTarget) {
      const patch = { name: form.name, role: form.role };
      if (form.password) patch.password = form.password;
      await api.patch(`/users/${editTarget.id}`, patch);
      toast.success("Teammate updated");
    } else {
      await api.post("/users", form);
      toast.success(`Added ${form.name}`);
    }
    load();
  };

  const doDelete = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will no longer be able to sign in.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Team"
        title="Users"
        description="Everyone who can sign in to the Ops Console. Admins can add, edit, or remove teammates."
        testId="users-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="users-print-btn" />
            {isAdmin ? (
              <Button
                onClick={() => { setEditTarget(null); setFormOpen(true); }}
                data-testid="new-user-btn"
                className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
              >
                <Plus size={16} className="mr-1.5" /> Add Teammate
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                <Lock size={14} /> Admin-only actions
              </div>
            )}
          </div>
        }
      />

      <div className="p-4 md:p-8 print-container">
        <PrintHeader title="Team Users" subtitle={`${users.length} teammate${users.length === 1 ? "" : "s"}`} />
        <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
          <table className="w-full text-sm min-w-[720px]" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="p-4">Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Role</th>
                <th className="p-4">Joined</th>
                <th className="p-4 w-16 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="p-12 text-center text-slate-500 font-mono text-sm">No teammates yet.</td></tr>
              ) : users.map((u) => {
                const { cls, Icon } = roleBadge(u.role);
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="p-4">
                      <div className="font-semibold text-slate-900">{u.name}</div>
                      {isSelf && <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold mt-0.5">You</div>}
                    </td>
                    <td className="p-4 font-mono text-xs text-slate-600">{u.email}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold ${cls}`}>
                        <Icon size={12} /> {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-xs text-slate-500">{formatDate(u.created_at)}</td>
                    <td className="p-4 text-right">
                      {isAdmin ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="User actions"
                              data-testid={`user-actions-${u.id}`}
                              className="h-8 w-8 rounded-sm hover:bg-slate-100"
                            >
                              <MoreHorizontal size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 rounded-sm">
                            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => { setEditTarget(u); setFormOpen(true); }} data-testid={`user-edit-${u.id}`}>
                              <Pencil size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={isSelf}
                              onClick={() => doDelete(u)}
                              className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              data-testid={`user-delete-${u.id}`}
                            >
                              <Trash2 size={14} className="mr-2" /> {isSelf ? "Can't remove yourself" : "Remove"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-xs text-slate-400 font-mono">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <UserDialog open={formOpen} onOpenChange={setFormOpen} user={editTarget} onSubmit={submit} />
    </div>
  );
}
